import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { downloadFile, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { CalculationBaseColumn } from "./calculationBaseColumnLabelsStore";
import type { TFetchStatus } from "./types";

export const fetchTerm = createAsyncThunk("bpTerms/term", async (id: number, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/terms/${id}`);
        return response.data.result;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const downloadDocument = createAsyncThunk(
    "bpTerms/downloadDocument",
    async (
        {
            id,
            userRole,
            file,
        }: {
            id: number;
            userRole: string;
            file: { mimetype: string; name: string };
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await toast.promise(
                axios.get(
                    `${
                        userRole === "beneficiary"
                            ? import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT
                            : import.meta.env.REACT_APP_API_ENDPOINT
                    }/${userRole === "beneficiary" ? "terms" : "term"}/${id}/agreement`,
                ),
                {
                    pending: `${t("ShowStatements.PendingDownload")}`,
                    success: `${t("ShowStatements.SuccessDownload")}`,
                    error: `${t("ShowStatements.ErrorDownload")}`,
                },
            );
            const linkSource = `data:${file.mimetype ?? "text/plain"};base64,${response.data}`;
            const fileName = file.name;
            downloadFile({ source: linkSource, fileName, isLink: true });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface TermAgreement {
    id: number;
    filename: string | null;
    storageId: string | null;
    type: "agreement" | "avenant";
    lastUpdate: string | Date | null;
    companyId: number;
}

export const fetchTermAgreements = createAsyncThunk(
    "bpTerms/fetchTermAgreements",
    async (termId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get<{ data: TermAgreement[] }>(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/terms/${termId}/agreements`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const downloadTermAgreement = createAsyncThunk(
    "bpTerms/downloadTermAgreement",
    async ({ termId, agreement }: { termId: number; agreement: TermAgreement }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(
                    import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT +
                        `/terms/${termId}/agreements/${agreement.id}`,
                ),
                {
                    pending: `${t("ShowStatements.PendingDownload")}`,
                    success: `${t("ShowStatements.SuccessDownload")}`,
                    error: `${t("ShowStatements.ErrorDownload")}`,
                },
            );
            const linkSource = `data:application/pdf;base64,${response.data}`;
            const fileName = agreement.filename?.split(".")[0] ?? `Contrat_${agreement.id}`;
            downloadFile({ source: linkSource, fileName, isLink: true });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

interface termWithChannel {
    advancePayment: number;
    author: string;
    channelData: {
        channelId: number;
        channelName: string;
        kind: number;
        type: number;
        // Columns the contract computes on: the milestone one tells whether thresholds are copies or cents.
        calculationBaseColumn: CalculationBaseColumn;
        milestoneBaseColumn: CalculationBaseColumn;
        data: {
            percentage: number;
            quantity: number | string;
        }[];
    }[];
    ean13: string;
    releaseDate: string;
    termId: number;
    title: string;
    originalFilemime: string | null;
    originalFilename: string | null;
    originalFilesize: string | null;
}

export interface TermState {
    term: termWithChannel | null;
    termStatus: TFetchStatus;
    termAgreements: TermAgreement[];
    termAgreementsStatus: TFetchStatus;
}

const initialState: TermState = {
    term: null,
    termStatus: "idle",
    termAgreements: [],
    termAgreementsStatus: "idle",
};

export const bpTermsSlice = createSlice({
    name: "bpTerms",
    initialState,
    reducers: {
        clearCurrentTerm(state) {
            state.term = null;
            state.termAgreements = [];
            state.termAgreementsStatus = "idle";
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchTerm.fulfilled, (state, action) => {
            const data = action.payload;
            data.channelData = data.channelData.map((channel: any) => {
                channel.data = JSON.parse(channel.data);
                return channel;
            });
            state.term = action.payload;
        });
        builder.addCase(fetchTerm.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchTermAgreements.pending, (state) => {
            state.termAgreementsStatus = "pending";
        });
        builder.addCase(fetchTermAgreements.fulfilled, (state, action) => {
            state.termAgreements = action.payload;
            state.termAgreementsStatus = "succeeded";
        });
        builder.addCase(fetchTermAgreements.rejected, (state) => {
            state.termAgreements = [];
            state.termAgreementsStatus = "failed";
        });

        builder.addCase(downloadDocument.fulfilled, (state, action) => {});
        builder.addCase(downloadDocument.rejected, (state, action) => {});
    },
});

export const { clearCurrentTerm } = bpTermsSlice.actions;

export default bpTermsSlice.reducer;
